import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
  useLayoutEffect,
  useRef,
} from 'react';
import s from './Field.module.css';

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  id: string;
  children: ReactNode;
  trailing?: ReactNode;
}

function Shell({ label, hint, error, id, children, trailing }: FieldShellProps) {
  return (
    <div className={s.field}>
      <label htmlFor={id} className={`${s.label} t-footnote`}>
        {label}
      </label>
      <div className={`${s.control} ${error ? s.invalid : ''}`}>
        {children}
        {trailing && <span className={s.trailing}>{trailing}</span>}
      </div>
      {error ? (
        <p className={`${s.error} t-footnote`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={`${s.hint} t-footnote`}>{hint}</p>
      ) : null}
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | null;
  trailing?: ReactNode;
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  trailing,
  className,
  ...rest
}: TextFieldProps) {
  const id = useId();
  return (
    <Shell label={label} hint={hint} error={error} id={id} trailing={trailing}>
      <input
        id={id}
        className={`${s.input} ${className ?? ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Shell>
  );
}

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  minRows?: number;
}

export function TextArea({ label, value, onChange, hint, minRows = 3, ...rest }: TextAreaProps) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: grow to fit whenever the text changes
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <Shell label={label} hint={hint} id={id}>
      <textarea
        id={id}
        ref={ref}
        rows={minRows}
        className={`${s.input} ${s.textarea}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </Shell>
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  hint?: ReactNode;
}

export function Select({ label, value, onChange, options, hint, ...rest }: SelectProps) {
  const id = useId();
  return (
    <Shell label={label} hint={hint} id={id}>
      <select
        id={id}
        className={`${s.input} ${s.select}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Shell>
  );
}

interface ToggleProps {
  label: ReactNode;
  detail?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function Toggle({ label, detail, checked, onChange }: ToggleProps) {
  const id = useId();
  return (
    <label htmlFor={id} className={s.toggleRow}>
      <span className={s.toggleText}>
        <span className={s.toggleLabel}>{label}</span>
        {detail && <span className="t-footnote muted">{detail}</span>}
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        className={s.switch}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

/** Two or three fields side by side that wrap on narrow screens. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className={s.row}>{children}</div>;
}
